import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Image,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Badge,
  SimpleGrid,
  Card,
  CardBody
} from '@chakra-ui/react';
import axios from 'axios';

const COMPREFACE_API_BASE = '/api/v1';
const COMPREFACE_STATIC_HOST = '/api/v1/static';
const API_KEYS = {
  recognition: 'ead1fc48-a19a-4c50-bd59-ecfb4bfa84d8',
  verification: 'f277d47c-0ac5-4e42-a331-c6584b0915bc'
};

interface Match {
  subject: string;
  similarity: number;
  image_url?: string;
  imageId?: string;
}

const CompreFaceMatchViewer: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [secondFile, setSecondFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [secondPreviewUrl, setSecondPreviewUrl] = useState<string | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [verificationResult, setVerificationResult] = useState<Match[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, isSecond = false) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isSecond) {
        setSecondFile(file);
        setSecondPreviewUrl(URL.createObjectURL(file));
      } else {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setMatches([]);
      }
    }
  };

  const handleImageClick = async (imageId: string) => {
    try {
      const response = await axios.get(
        `${COMPREFACE_API_BASE}/recognition/faces/${imageId}/img`,
        {
          headers: { 'x-api-key': API_KEYS.recognition },
          responseType: 'blob'
        }
      );
      
      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
      
      // Clean up the blob URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error fetching image:', error);
      toast({
        title: 'Error',
        description: 'Failed to open image',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const fetchImageUrlForSubject = async (subject: string): Promise<{ imageId: string; previewUrl: string } | undefined> => {
    try {
      const res = await axios.get(
        `${COMPREFACE_API_BASE}/recognition/faces?page=0&size=1&subject=${encodeURIComponent(subject)}`,
        {
          headers: { 'x-api-key': API_KEYS.recognition }
        }
      );
  
      const faces = res.data.faces;
      if (faces.length > 0) {
        const imageId = faces[0].image_id;
        // Create a preview URL using the static host
        const previewUrl = `${COMPREFACE_STATIC_HOST}/${API_KEYS.recognition}/images/${imageId}`;
        return { imageId, previewUrl };
      }
    } catch (error) {
      console.warn(`No image found for subject ${subject}:`, error);
    }
    return undefined;
  };
  

  const handleRecognition = async () => {
    if (!selectedFile) {
      toast({
        title: 'Error',
        description: 'Please select an image first',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('prediction_count', '10');
    formData.append('similarity_threshold', '0.75');
    formData.append('det_prob_threshold', '0.1');

    try {
      const response = await axios.post(
        `${COMPREFACE_API_BASE}/recognition/recognize`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': API_KEYS.recognition,
          },
        }
      );

      const rawMatches: Match[] = [];
      for (const face of response.data.result) {
        if (face.subjects) {
          for (const s of face.subjects) {
            if (s.similarity >= 0.75) {
              rawMatches.push({ subject: s.subject, similarity: s.similarity });
            }
          }
        }
      }

      const enrichedMatches = await Promise.all(
        rawMatches.map(async (m) => {
          const imageData = await fetchImageUrlForSubject(m.subject);
          return {
            ...m,
            imageId: imageData?.imageId,
            image_url: imageData?.previewUrl
          };
        })
      );

      setMatches(enrichedMatches);
    } catch (error) {
      console.error('Recognition error:', error);
      toast({
        title: 'Error',
        description: 'Failed to process image',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async () => {
    if (!selectedFile || !secondFile) {
      toast({
        title: 'Error',
        description: 'Please select both images',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('source_image', selectedFile);
    formData.append('target_image', secondFile);
    formData.append('det_prob_threshold', '0.1');

    try {
      const response = await axios.post(
        `${COMPREFACE_API_BASE}/verification/verify`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-api-key': API_KEYS.verification,
          },
        }
      );

      const matches: Match[] = response.data.result[0]?.face_matches.map((m: any) => ({
        subject: 'Target Face',
        similarity: m.similarity
      })) || [];
      setVerificationResult(matches);
    } catch (error) {
      console.error('Verification error:', error);
      toast({
        title: 'Error',
        description: 'Verification failed',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={6}>
      <Tabs>
        <TabList>
          <Tab>Face Recognition</Tab>
          <Tab>Face Verification</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading size="lg">CompreFace Recognition</Heading>
              <FormControl>
                <FormLabel>Upload Image</FormLabel>
                <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e)} />
              </FormControl>
              {previewUrl && (
                <Box><Image src={previewUrl} maxH="600px" w="auto" /></Box>
              )}
              <Button colorScheme="blue" onClick={handleRecognition} isLoading={loading}>
                Recognize Faces
              </Button>
              {matches.length > 0 && (
                <Box mt={6}>
                  <Heading size="md" mb={4}>Matches (≥ 75%)</Heading>
                  <SimpleGrid columns={[1, 2, 3]} spacing={4}>
                    {matches.map((match, idx) => (
                      <Card key={idx} borderWidth="1px" borderRadius="lg">
                        <CardBody>
                          {match.image_url ? (
                            <Box 
                              onClick={() => match.imageId && handleImageClick(match.imageId)}
                              cursor="pointer"
                              _hover={{ opacity: 0.8 }}
                            >
                              <Image
                                src={match.image_url}
                                alt={match.subject}
                                borderRadius="md"
                              />
                            </Box>
                          ) : (
                            <Text>No image available</Text>
                          )}
                          <Text mt={2} fontWeight="bold">{match.subject}</Text>
                          <Badge colorScheme="green">{(match.similarity * 100).toFixed(2)}%</Badge>
                        </CardBody>
                      </Card>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading size="lg">CompreFace 1:1 Verification</Heading>
              <FormControl>
                <FormLabel>Source Image</FormLabel>
                <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e)} />
              </FormControl>
              <FormControl>
                <FormLabel>Target Image</FormLabel>
                <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e, true)} />
              </FormControl>
              <Flex gap={4} justify="center">
                {previewUrl && (
                  <Box maxW="300px">
                    <Text fontWeight="bold">Source</Text>
                    <Image src={previewUrl} maxH="300px" w="auto" />
                  </Box>
                )}
                {secondPreviewUrl && (
                  <Box maxW="300px">
                    <Text fontWeight="bold">Target</Text>
                    <Image src={secondPreviewUrl} maxH="300px" w="auto" />
                  </Box>
                )}
              </Flex>
              <Button colorScheme="blue" onClick={handleVerification} isLoading={loading}>
                Verify Match
              </Button>
              {verificationResult.length > 0 && (
                <Box mt={6}>
                  <Heading size="md" mb={4}>Verification Results</Heading>
                  <VStack spacing={3} align="start">
                    {verificationResult.map((res, idx) => (
                      <Text key={idx}>Match Similarity: {(res.similarity * 100).toFixed(2)}%</Text>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CompreFaceMatchViewer;
